/**
 * Commission Detail Page
 *
 * Full view of a single commission: brief, patron thread, quote editor with a
 * live rev-share split preview, guarded status transitions, and the patron
 * portal link.
 */

import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ArrowLeft, Send, Copy, User, Bot, Palette, FileText, ExternalLink, RotateCcw } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import {
  useCommission,
  useUpdateCommission,
  useCreateCommissionMessage,
  useCreateInvoice,
  useApproveDraft,
  useDismissDraft,
  useRefundCommission,
} from '../hooks'
import { CommissionStatus, InvoiceKind, CommissionPayment } from '../types'

const PORTAL_BASE = 'https://www.myarthaus.com/commissions'

const statusLabel: Record<CommissionStatus, string> = {
  [CommissionStatus.NEW]: 'New',
  [CommissionStatus.SCOPING]: 'Scoping',
  [CommissionStatus.QUOTED]: 'Quoted',
  [CommissionStatus.DEPOSIT_DUE]: 'Deposit Due',
  [CommissionStatus.DEPOSIT_PAID]: 'Deposit Paid',
  [CommissionStatus.IN_PROGRESS]: 'In Progress',
  [CommissionStatus.FINAL_DUE]: 'Final Due',
  [CommissionStatus.FINAL_PAID]: 'Final Paid',
  [CommissionStatus.DELIVERED]: 'Delivered',
  [CommissionStatus.COMPLETED]: 'Completed',
  [CommissionStatus.DECLINED]: 'Declined',
  [CommissionStatus.CANCELLED]: 'Cancelled',
  [CommissionStatus.REFUNDED]: 'Refunded',
}

// Mirrors lib/commissions/state-machine.js — the artist can only pick a legal
// next status (payment-driven transitions happen server-side, not from here).
const TRANSITIONS: Record<CommissionStatus, CommissionStatus[]> = {
  [CommissionStatus.NEW]: [CommissionStatus.SCOPING, CommissionStatus.QUOTED, CommissionStatus.DECLINED, CommissionStatus.CANCELLED],
  [CommissionStatus.SCOPING]: [CommissionStatus.QUOTED, CommissionStatus.DECLINED, CommissionStatus.CANCELLED],
  [CommissionStatus.QUOTED]: [CommissionStatus.SCOPING, CommissionStatus.DECLINED, CommissionStatus.CANCELLED],
  [CommissionStatus.DEPOSIT_DUE]: [CommissionStatus.CANCELLED],
  [CommissionStatus.DEPOSIT_PAID]: [CommissionStatus.IN_PROGRESS, CommissionStatus.CANCELLED],
  [CommissionStatus.IN_PROGRESS]: [CommissionStatus.FINAL_DUE, CommissionStatus.CANCELLED],
  [CommissionStatus.FINAL_DUE]: [CommissionStatus.CANCELLED],
  [CommissionStatus.FINAL_PAID]: [CommissionStatus.DELIVERED],
  [CommissionStatus.DELIVERED]: [CommissionStatus.COMPLETED],
  [CommissionStatus.COMPLETED]: [],
  [CommissionStatus.DECLINED]: [],
  [CommissionStatus.CANCELLED]: [],
  [CommissionStatus.REFUNDED]: [],
}

function money(value?: number | string | null, currency = 'USD'): string {
  if (value === null || value === undefined || value === '') return '—'
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value))
}

function senderIcon(senderType: string) {
  if (senderType === 'artist') return <Palette className="h-4 w-4" />
  if (senderType === 'picasso') return <Bot className="h-4 w-4" />
  return <User className="h-4 w-4" />
}

function PaymentRow({
  label,
  payment,
  currency,
}: {
  label: string
  payment?: CommissionPayment
  currency: string
}) {
  if (!payment) {
    return (
      <div className="flex justify-between">
        <span className="text-muted-foreground">{label}</span>
        <span className="text-muted-foreground">—</span>
      </div>
    )
  }

  const amount = money(payment.presentmentAmount ?? payment.grossAmount, payment.presentmentCurrency || currency)

  return (
    <div className="space-y-1">
      <div className="flex justify-between">
        <span className="text-muted-foreground">{label}</span>
        <span>
          {amount} · <span className="capitalize">{payment.status}</span>
        </span>
      </div>
      {payment.status === 'invoiced' && payment.checkoutUrl && (
        <a
          href={payment.checkoutUrl}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline inline-flex items-center gap-1 text-xs"
        >
          Open checkout <ExternalLink className="h-3 w-3" />
        </a>
      )}
      {payment.status === 'captured' && payment.settledAmount != null && (
        <p className="text-xs text-muted-foreground">Settled {money(payment.settledAmount, 'GBP')}</p>
      )}
    </div>
  )
}

export function CommissionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const commissionId = parseInt(id || '0')
  const { data: commission, isLoading } = useCommission(commissionId)
  const updateCommission = useUpdateCommission()
  const createMessage = useCreateCommissionMessage()
  const createInvoice = useCreateInvoice()
  const approveDraft = useApproveDraft()
  const dismissDraft = useDismissDraft()
  const refundCommission = useRefundCommission()

  const [refundOpen, setRefundOpen] = useState(false)
  const [refundReason, setRefundReason] = useState('')

  const [quoteAmount, setQuoteAmount] = useState('')
  const [depositAmount, setDepositAmount] = useState('')
  const [artworkCount, setArtworkCount] = useState('1')
  const [reply, setReply] = useState('')

  useEffect(() => {
    if (commission) {
      setQuoteAmount(commission.quoteAmount != null ? String(commission.quoteAmount) : '')
      setDepositAmount(commission.depositAmount != null ? String(commission.depositAmount) : '')
      setArtworkCount(String(commission.artworkCount ?? 1))
    }
  }, [commission])

  if (isLoading || !commission) {
    return (
      <div className="space-y-6 p-8">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const currency = commission.currency || 'USD'
  const allowedNext = TRANSITIONS[commission.status] || []

  const saveQuote = () => {
    updateCommission.mutate({
      id: commissionId,
      data: {
        quoteAmount: quoteAmount === '' ? undefined : Number(quoteAmount),
        depositAmount: depositAmount === '' ? undefined : Number(depositAmount),
        artworkCount: artworkCount === '' ? undefined : Number(artworkCount),
      },
    })
  }

  const changeStatus = (status: CommissionStatus) => {
    updateCommission.mutate({ id: commissionId, data: { status } })
  }

  const sendReply = () => {
    if (!reply.trim()) return
    createMessage.mutate(
      { id: commissionId, bodyText: reply.trim() },
      { onSuccess: () => setReply('') }
    )
  }

  const copyPortalLink = () => {
    if (!commission.accessToken) return
    const url = `${PORTAL_BASE}/${commission.accessToken}`
    navigator.clipboard.writeText(url)
    toast({ title: 'Link copied', description: 'Patron portal link copied to clipboard.' })
  }

  const sendInvoice = (kind: InvoiceKind) => {
    createInvoice.mutate({ id: commissionId, kind })
  }

  const depositPayment = commission.payments.find((p) => p.kind === 'deposit')
  const finalPayment = commission.payments.find((p) => p.kind === 'final')
  const settlement = commission.settlement

  const canRefund = ['deposit_paid', 'final_paid', 'delivered'].includes(commission.status)
  const doRefund = () => {
    refundCommission.mutate(
      { id: commissionId, reason: refundReason.trim() || undefined },
      { onSuccess: () => { setRefundOpen(false); setRefundReason('') } }
    )
  }

  const brief = commission.brief || {}
  const briefEntries = Object.entries(brief).filter(([, v]) => v !== null && v !== undefined && v !== '')

  return (
    <div className="space-y-6 p-8">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/app/easel/commissions')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-semibold tracking-tight">
              {commission.title || 'Untitled commission'}
            </h1>
            <Badge>{statusLabel[commission.status]}</Badge>
            <Badge variant="outline">{commission.source}</Badge>
          </div>
          <p className="text-sm text-muted-foreground font-mono mt-1">{commission.reference}</p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main column: brief + thread */}
        <div className="lg:col-span-2 space-y-6">
          {/* Brief */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Brief</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {briefEntries.length === 0 ? (
                <p className="text-sm text-muted-foreground">No brief details captured.</p>
              ) : (
                briefEntries.map(([key, value]) => (
                  <div key={key} className="grid grid-cols-3 gap-2 text-sm">
                    <span className="text-muted-foreground capitalize">{key}</span>
                    <span className="col-span-2 whitespace-pre-wrap">{String(value)}</span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Thread */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Conversation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {commission.thread.filter((m) => m.status !== 'dismissed').length === 0 ? (
                <p className="text-sm text-muted-foreground">No messages yet.</p>
              ) : (
                commission.thread
                  .filter((m) => m.status !== 'dismissed')
                  .map((m) => {
                    const isDraft = m.status === 'draft'
                    return (
                      <div
                        key={m.id}
                        className={`rounded-lg border p-3 ${
                          isDraft
                            ? 'bg-amber-500/10 border-amber-500/40'
                            : m.senderType === 'artist'
                              ? 'bg-primary/5 border-primary/20'
                              : 'bg-muted/40'
                        }`}
                      >
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                          {senderIcon(m.senderType)}
                          <span className="capitalize font-medium text-foreground">{m.senderType}</span>
                          {isDraft && (
                            <Badge variant="outline" className="border-amber-500/50 text-amber-700">
                              Draft · needs approval
                            </Badge>
                          )}
                          <span>·</span>
                          <span>{new Date(m.createdAt).toLocaleString()}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{m.bodyText}</p>
                        {isDraft && (
                          <div className="flex items-center gap-2 mt-3">
                            <Button
                              size="sm"
                              disabled={approveDraft.isPending}
                              onClick={() => approveDraft.mutate({ id: commissionId, messageId: m.id })}
                            >
                              <Send className="h-3 w-3 mr-1" />
                              Approve &amp; send
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={dismissDraft.isPending}
                              onClick={() => dismissDraft.mutate({ id: commissionId, messageId: m.id })}
                            >
                              Dismiss
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setReply(m.bodyText || '')}>
                              Edit
                            </Button>
                          </div>
                        )}
                      </div>
                    )
                  })
              )}

              <Separator />

              <div className="space-y-2">
                <Label htmlFor="reply">Reply to patron</Label>
                <Textarea
                  id="reply"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder="Write a message…"
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button onClick={sendReply} disabled={!reply.trim() || createMessage.isPending}>
                    <Send className="h-4 w-4 mr-2" />
                    Send
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Side column: quote, split, status, portal */}
        <div className="space-y-6">
          {/* Quote editor */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Quote</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="quote">Quote amount ({currency})</Label>
                <Input id="quote" type="number" value={quoteAmount} onChange={(e) => setQuoteAmount(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="deposit">Deposit amount ({currency})</Label>
                <Input id="deposit" type="number" value={depositAmount} onChange={(e) => setDepositAmount(e.target.value)} />
              </div>
              <div className="space-y-1">
                <Label htmlFor="artworks">Artwork count</Label>
                <Input id="artworks" type="number" min="1" value={artworkCount} onChange={(e) => setArtworkCount(e.target.value)} />
              </div>
              <Button className="w-full" onClick={saveQuote} disabled={updateCommission.isPending}>
                Save quote
              </Button>
            </CardContent>
          </Card>

          {/* Split preview */}
          {commission.split && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Revenue split</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Gross</span>
                  <span>{money(commission.split.grossAmount, commission.split.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">
                    Platform ({Math.round(commission.split.platformPct * 100)}%)
                  </span>
                  <span>−{money(commission.split.platformFee, commission.split.currency)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Base fee</span>
                  <span>−{money(commission.split.baseFee, commission.split.currency)}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>You receive</span>
                  <span>{money(commission.split.artistNet, commission.split.currency)}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Payments */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Payments</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <PaymentRow label="Deposit" payment={depositPayment} currency={currency} />
              <PaymentRow label="Balance" payment={finalPayment} currency={currency} />

              {commission.status === 'quoted' && !depositPayment && (
                <Button className="w-full" onClick={() => sendInvoice('deposit')} disabled={createInvoice.isPending}>
                  <FileText className="h-4 w-4 mr-2" />
                  Send deposit invoice
                </Button>
              )}
              {commission.status === 'in_progress' && !finalPayment && (
                <Button className="w-full" onClick={() => sendInvoice('final')} disabled={createInvoice.isPending}>
                  <FileText className="h-4 w-4 mr-2" />
                  Send balance invoice
                </Button>
              )}
              <p className="text-xs text-muted-foreground">
                Patrons are charged in USD via Shopify checkout; payout settles in GBP.
              </p>
            </CardContent>
          </Card>

          {/* Settlement / payout */}
          {settlement && settlement.settledCollected > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Settlement</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Collected (USD)</span>
                  <span>{money(settlement.presentmentCollected, 'USD')}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Settled (GBP)</span>
                  <span>{money(settlement.settledCollected, 'GBP')}</span>
                </div>
                <Separator />
                <div className="flex justify-between font-semibold">
                  <span>Your payout (GBP)</span>
                  <span>{settlement.artistPayoutGbp != null ? money(settlement.artistPayoutGbp, 'GBP') : '—'}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Status transitions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Status</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm">
                Current: <span className="font-medium">{statusLabel[commission.status]}</span>
              </p>
              {allowedNext.length === 0 ? (
                <p className="text-sm text-muted-foreground">No further transitions available.</p>
              ) : (
                <Select onValueChange={(value) => changeStatus(value as CommissionStatus)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Move to…" />
                  </SelectTrigger>
                  <SelectContent>
                    {allowedNext.map((s) => (
                      <SelectItem key={s} value={s}>
                        {statusLabel[s]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {canRefund && (
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full text-destructive hover:text-destructive"
                  onClick={() => setRefundOpen(true)}
                >
                  <RotateCcw className="h-4 w-4 mr-2" />
                  Refund commission
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Patron portal */}
          {commission.accessToken && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Patron portal</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Share this private link with the patron to view and reply.
                </p>
                <Button variant="outline" className="w-full" onClick={copyPortalLink}>
                  <Copy className="h-4 w-4 mr-2" />
                  Copy patron link
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={refundOpen} onOpenChange={setRefundOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Refund commission</DialogTitle>
            <DialogDescription>
              This marks {commission.reference} refunded and removes it from payout. The patron's
              money-back is issued in Shopify (automatic refund is off by default).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="refund-reason">Reason (optional)</Label>
            <Textarea
              id="refund-reason"
              value={refundReason}
              onChange={(e) => setRefundReason(e.target.value)}
              placeholder="Why is this being refunded?"
              rows={2}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundOpen(false)}>
              Cancel
            </Button>
            <Button variant="destructive" disabled={refundCommission.isPending} onClick={doRefund}>
              Refund
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
